import { forwardRef, useState, useEffect } from 'react';
import { capitalize, isEmpty } from 'lodash';
import { Avatar as MuiAvatar } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { getCoverImage, getImageThumbUrl } from '@sda/utils/image';
import { getNameParts } from '@sda/utils/string';
import { stringToHsl } from '@sda/utils/style';
import type { ImageInput } from '@sda/utils/image/interfaces';
import type { AvatarProps as MuiAvatarProps } from '@mui/material';

const SIZE_SMALL = 33;
const SIZE_MEDIUM = 40;

const useStyles = makeStyles({ name: 'UserAvatar' })(() => ({
  root: {
    fontSize: 16,
    textDecoration: 'none',
    height: SIZE_MEDIUM,
    width: SIZE_MEDIUM,
  },
  interactive: {
    cursor: 'pointer',

    '&:hover': {
      opacity: 0.6,
      transition: '0.5s',
    },
  },
  sizeMedium: {

  },
  sizeSmall: {
    height: SIZE_SMALL,
    width: SIZE_SMALL,
  },
}));

interface UserAvatarProps extends MuiAvatarProps {
  readonly onClick?: (...args: any[]) => any;
  readonly className?: string;
  readonly size?: 'small' | 'medium';
  readonly emailHash: string;
  readonly images?: ImageInput;
  readonly displayName?: string;
}

export const UserAvatar = forwardRef<HTMLImageElement, UserAvatarProps>(({
  emailHash, images, displayName, onClick,
  className = '',
  size = 'medium',
  ...restProps
}, ref) => {
  const { classes, cx } = useStyles();

  const avatarClasses = cx(
    classes.root,
    onClick && classes.interactive,
    className,
    classes[`size${capitalize(size)}`],
  );

  const [hasGravatarError, setGravatarError] = useState(false);

  useEffect(() => setGravatarError(false), [emailHash]);

  let extraProps = {};

  if (onClick) {
    extraProps = {
      role: 'button',
      onClick,
    };
  }

  const backgroundColor = stringToHsl(emailHash || displayName || 'N/A');

  if (isEmpty(images)) {
    const { firstName: first, lastName: last } = getNameParts(displayName);

    const initialsString = `${first.charAt(0)}${(last) ? last.charAt(0) : first.charAt(1)}`.toUpperCase();
    const gravatarUrl = emailHash ? `https://www.gravatar.com/avatar/${emailHash}?d=404&s=${size === 'small' ? SIZE_SMALL : SIZE_MEDIUM}` : null;

    return (
      <MuiAvatar
        className={avatarClasses}
        ref={ref}
        alt={displayName || emailHash}
        src={!hasGravatarError && gravatarUrl ? getImageThumbUrl(gravatarUrl) : null}
        onError={() => setGravatarError(true)}
        style={{ backgroundColor }}
        {...restProps}
      >
        {initialsString}
      </MuiAvatar>
    );
  }

  return (
    <MuiAvatar
      className={avatarClasses}
      ref={ref}
      alt={displayName || emailHash}
      src={getCoverImage(images, null, getImageThumbUrl)}
      style={{ backgroundColor }}
      {...extraProps}
      {...restProps}
    />
  );
});
